import { useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { Turnstile as CloudflareTurnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

// Environment configuration for Cloudflare Turnstile
// Test key for development - always passes
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
const IS_DEVELOPMENT = import.meta.env.DEV;

export interface TurnstileRef {
  reset: () => void;
  execute: () => void;
  getResponse: () => string | null;
}

export type TurnstileSize = 'normal' | 'compact' | 'invisible' | 'flexible';

interface TurnstileProps {
  onVerify: (token: string) => void;
  onError?: (error: Error | unknown) => void;
  onExpire?: () => void;
  onLoad?: () => void;
  className?: string;
  size?: TurnstileSize;
}

export const Turnstile = forwardRef<TurnstileRef, TurnstileProps>(
  ({ onVerify, onError, onExpire, onLoad, className = '', size = 'normal' }, ref) => {
    const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);
    const tokenRef = useRef<string | null>(null);
    const [isVerified, setIsVerified] = useState(false);
    const [hasError, setHasError] = useState(false);

    useImperativeHandle(ref, () => ({
      reset: () => {
        turnstileRef.current?.reset();
        tokenRef.current = null;
        setHasError(false);
        setIsVerified(false);
      },
      execute: () => {
        // Turnstile auto-executes, but we can trigger reset and re-render
        turnstileRef.current?.reset();
      },
      getResponse: () => {
        return tokenRef.current;
      },
    }));

    const handleVerify = (token: string) => {
      tokenRef.current = token;
      setIsVerified(true);
      setHasError(false);
      onVerify(token);
    };

    const handleError = (error: Error | unknown) => {
      console.error('Turnstile error:', error);
      tokenRef.current = null;
      setIsVerified(false);
      setHasError(true);
      onError?.(error);
    };

    const handleExpire = () => {
      console.warn('Turnstile expired');
      tokenRef.current = null;
      setIsVerified(false);
      setHasError(false);
      onExpire?.();
    };

    const handleWidgetLoad = () => {
      // Don't set isLoading to false here - keep showing spinner until verification completes
      // The widget has loaded but verification is still in progress
      onLoad?.();
    };

    // For invisible mode, don't show the card UI
    if (size === 'invisible') {
      return (
        <div className={className}>
          <CloudflareTurnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={handleVerify}
            onError={handleError}
            onExpire={handleExpire}
            onWidgetLoad={handleWidgetLoad}
            options={{
              theme: 'light',
              size: size,
            }}
          />
        </div>
      );
    }

    return (
      <div className={`flex flex-col items-center ${className}`}>
        {IS_DEVELOPMENT && TURNSTILE_SITE_KEY === '1x00000000000000000000AA' && (
          <div className="mb-2 text-xs text-gray-500 text-center">
            Development mode: Using test Turnstile key
          </div>
        )}
        
        {/* Security verification card - similar to SendGrid's Cloudflare widget */}
        <div className="w-full max-w-[340px] bg-gray-50 border border-gray-200 rounded-lg px-5 py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Left side - Status */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Loading state - show until verified or error */}
              {!isVerified && !hasError && (
                <>
                  <div className="relative w-7 h-7">
                    {/* Outer ring */}
                    <div className="absolute inset-0 border-2 border-gray-200 rounded-full"></div>
                    {/* Spinning arc */}
                    <div className="absolute inset-0 border-2 border-transparent border-t-orange-500 rounded-full animate-spin"></div>
                  </div>
                  <span className="text-gray-700 font-medium text-base">Verifying...</span>
                </>
              )}
              
              {/* Success state */}
              {isVerified && !hasError && (
                <>
                  <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium text-base">Success!</span>
                </>
              )}
              
              {/* Error state */}
              {hasError && (
                <>
                  <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-red-600 font-medium">Error</span>
                    <button
                      type="button"
                      onClick={() => {
                        setHasError(false);
                        turnstileRef.current?.reset();
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 underline text-left"
                    >
                      Try again
                    </button>
                  </div>
                </>
              )}
            </div>
            
            {/* Right side - Cloudflare branding */}
            <div className="flex flex-col items-center flex-shrink-0">
              {/* Cloudflare cloud logo */}
              <svg className="w-10 h-7 mb-0.5" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M68.3 31.7H24.6c-7.5 0-13.5-6-13.5-13.5 0-6.4 4.5-11.8 10.5-13.1C23.5 2.2 27.8 0 32.6 0c5.8 0 10.8 3.4 13.2 8.3 1.5-.7 3.2-1.1 4.9-1.1 6.3 0 11.4 5.1 11.4 11.4 0 .9-.1 1.7-.3 2.5 4.8 1.3 8.3 5.7 8.3 10.8 0 6.3-5.1 11.4-11.4 11.4h-9.5" fill="#F6821F"/>
                <path d="M72.3 31.7c4.6 0 8.4-3.8 8.4-8.4 0-3.9-2.7-7.2-6.3-8.1.1-.6.2-1.3.2-1.9 0-5-4-9-9-9-1.3 0-2.6.3-3.7.8C59.8 1.7 55.5-1 50.6-1c-6.8 0-12.3 5.5-12.3 12.3 0 .5 0 1 .1 1.5C33.6 13.5 30 17.5 30 22.3c0 5.2 4.2 9.4 9.4 9.4h32.9z" fill="#FAAD3F"/>
              </svg>
              <span className="text-[11px] font-bold text-gray-600 tracking-wide">CLOUDFLARE</span>
              <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-0.5">
                <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  Privacy
                </a>
                <span>·</span>
                <a href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  Terms
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hidden Turnstile widget - we use our custom UI above */}
        <div className="hidden">
          <CloudflareTurnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={handleVerify}
            onError={handleError}
            onExpire={handleExpire}
            onWidgetLoad={handleWidgetLoad}
            options={{
              theme: 'light',
              size: 'invisible',
            }}
          />
        </div>
      </div>
    );
  }
);

Turnstile.displayName = 'Turnstile';

